import { useEffect, useState, type ReactNode } from "react";
import type { IconSvgElement } from "@hugeicons/react";
import { HugeiconsIcon } from "@hugeicons/react";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Button, ButtonVariant } from "shared/components/Button";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components/IconButton";
import { Modal } from "shared/components";
import { BaseInput } from "shared/components/forms/BaseInput";
import { Hide } from "shared/components/helpers/Hide";
import { Show } from "shared/components/helpers/Show";
import { cx } from "shared/utils";

export type ConfirmDialogVariant =
  | "danger"
  | "primary"
  | "warning"
  | "success"
  | "base";

export type ConfirmDialogButtonsDirection =
  | "flex-row"
  | "flex-row-reverse"
  | "flex-col"
  | "flex-col-reverse";

const iconVariantStyles: Record<
  ConfirmDialogVariant,
  { bg: string; color: string }
> = {
  danger: { bg: "bg-danger-50", color: "text-danger-500" },
  primary: { bg: "bg-primary-50", color: "text-primary-500" },
  warning: { bg: "bg-warning-50", color: "text-warning-500" },
  success: { bg: "bg-success-50", color: "text-success-500" },
  base: { bg: "bg-alpha-40", color: "text-content-800" }
};

const confirmVariantMap: Record<ConfirmDialogVariant, ButtonVariant> = {
  danger: ButtonVariant.DANGER,
  primary: ButtonVariant.PRIMARY,
  warning: ButtonVariant.PRIMARY,
  success: ButtonVariant.PRIMARY,
  base: ButtonVariant.PRIMARY
};

interface ConfirmDialogIcon {
  icon: IconSvgElement;
  variant?: ConfirmDialogVariant;
}

interface ConfirmDialogButton {
  label: string;
  loading?: boolean;
  disabled?: boolean;
}

/**
 * Extra friction for destructive actions at scale: the confirm button stays disabled
 * until the user types `expected` verbatim (case-sensitive).
 */
interface ConfirmDialogConfirmationInput {
  label: string;
  expected: string;
  placeholder?: string;
}

interface ConfirmDialogProps {
  opened: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description?: ReactNode;
  confirmationInput?: ConfirmDialogConfirmationInput;
  variant?: ConfirmDialogVariant;
  icon?: ConfirmDialogIcon;
  confirmButton?: ConfirmDialogButton;
  cancelButton?: ConfirmDialogButton;
  hideCancelButton?: boolean;
  showCloseButton?: boolean;
  buttonsDirection?: ConfirmDialogButtonsDirection;
  widthClassName?: string;
  zIndex?: number;
}

export const ConfirmDialog = ({
  opened,
  onClose,
  onConfirm,
  title,
  description,
  confirmationInput,
  variant = "danger",
  icon,
  confirmButton,
  cancelButton,
  hideCancelButton,
  showCloseButton,
  buttonsDirection = "flex-row",
  widthClassName = "max-w-[366px]",
  zIndex
}: ConfirmDialogProps) => {
  const iconVariant = icon?.variant ?? variant;
  const iconStyles = iconVariantStyles[iconVariant];
  const buttonVariant = confirmVariantMap[variant];

  const [typedConfirmation, setTypedConfirmation] = useState("");
  // Start every open from a blank field so a previous confirmation can't carry over.
  useEffect(() => {
    if (opened) setTypedConfirmation("");
  }, [opened]);
  const confirmationSatisfied =
    !confirmationInput ||
    typedConfirmation.trim() === confirmationInput.expected;
  const confirmDisabled = confirmButton?.disabled || !confirmationSatisfied;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      panelClassName={cx(widthClassName, "w-full p-6")}
      zIndex={zIndex}
    >
      <Show if={showCloseButton}>
        <div className="flex justify-end">
          <IconButton
            variant={IconButtonVariant.TEXT_BASE}
            HugeIcon={Cancel01Icon}
            onClick={onClose}
            size={IconButtonSize.LG}
          />
        </div>
      </Show>

      {icon ? (
        <div
          className={cx(
            "size-16 flex items-center justify-center rounded-full mb-6 mx-auto",
            iconStyles.bg
          )}
        >
          <HugeiconsIcon
            className={iconStyles.color}
            icon={icon.icon}
            size={32}
          />
        </div>
      ) : null}

      <h1 className="text-xl font-bold text-content-1000 text-center">
        {title}
      </h1>

      <Show if={Boolean(description)}>
        <p className="text-sm font-medium text-content-400 mt-2 text-center">
          {description}
        </p>
      </Show>

      {confirmationInput ? (
        <div className="mt-4">
          <BaseInput
            id="confirm-dialog-confirmation-input"
            label={confirmationInput.label}
            hideOptional
            autoFocus
            autoComplete="off"
            placeholder={
              confirmationInput.placeholder ?? confirmationInput.expected
            }
            value={typedConfirmation}
            onChange={setTypedConfirmation}
            onKeyDown={(e) => {
              if (
                e.key === "Enter" &&
                confirmationSatisfied &&
                !confirmButton?.loading
              )
                onConfirm();
            }}
          />
        </div>
      ) : null}

      <div className={cx("flex gap-2 mt-6", buttonsDirection)}>
        <Hide if={hideCancelButton}>
          <Button
            className="w-full"
            loading={cancelButton?.loading}
            disabled={cancelButton?.disabled}
            onClick={onClose}
          >
            {cancelButton?.label ?? "Cancel"}
          </Button>
        </Hide>
        <Button
          className="w-full"
          variant={buttonVariant}
          loading={confirmButton?.loading}
          disabled={confirmDisabled}
          onClick={onConfirm}
        >
          {confirmButton?.label ?? "Confirm"}
        </Button>
      </div>
    </Modal>
  );
};
